import React from "react";
import "./CollaboratorDialog.css";
import {
    AppendableCollaboratorType,
    CollaboratorList,
    isAppendableCollaboratorType, isChangeableCollaboratorType
} from "../../models/Collaboration/CollaboratorList";
import {useUserInfo} from "../../hooks/useUserInfo";
import {
    Collaborator
} from "../../models/Collaboration/Collaborator";
import {debug_log, is_dev} from "../../utils/debug";
import {DropMenu} from "../JourneyMapEditor/DropMenu";
import {useTranslation} from "react-i18next";
import {applyMapCollaborators, findUserFromEmail, listMapCollaborators} from "../../graphql/Collaborator";
import {MessageDialog, MessageDialogHandles} from "../common/MessageDialog";

type CollaboratorDialogProps = {
    mapId: string;
    show: boolean;
    onClose: () => void;
}

const DEFAULT_INPUT_USER = '';
const DEFAULT_INPUT_TYPE = 'editor';

export const CollaboratorDialog = (props: CollaboratorDialogProps) => {

    const {t} = useTranslation();
    const {userName, userEmail} = useUserInfo();

    const ref = React.useRef<HTMLDialogElement>(null);
    const message_dialog = React.useRef<MessageDialogHandles>(null)
    const [input_user, setInputUser] = React.useState<string>(DEFAULT_INPUT_USER);
    const [input_type, setInputType] = React.useState<AppendableCollaboratorType>(DEFAULT_INPUT_TYPE);
    const [collaborator_list, setCollaboratorList] = React.useState<CollaboratorList>(CollaboratorList.createEmpty());
    const [is_loading, setIsLoading] = React.useState<boolean>(true);


    React.useEffect(() => {
        if (ref.current === null) {
            return;
        }

        if (props.show) {
            !ref.current.open && ref.current.showModal();
        } else {
            ref.current.open && ref.current.close();
        }
    }, [props.show]);

    React.useEffect(() => {
        if (!userName || !userEmail) {
            return;
        }

        setIsLoading(true);

        const created_collaborator_list = CollaboratorList.createEmpty().addCollaborator(new Collaborator(userName, userEmail, 'owner'));
        setCollaboratorList(created_collaborator_list);

        listMapCollaborators(props.mapId)
            .then(collaborators => {
                if (0 < collaborators.length) {
                    setCollaboratorList(created_collaborator_list.mergeArray(collaborators));
                }
                setIsLoading(false);
            })
            .catch((res) => {
                debug_log(res);
            });

    }, [props.mapId, userEmail, userName]);


    const handleAddCollaborator = async () => {
        if (is_loading) {
            return;
        }

        const find_user = input_user.trim();
        const append_type = input_type;

        if (find_user === '') {
            return;
        }

        setInputUser(DEFAULT_INPUT_USER); // ２度押し防止

        const append_user = await findUserFromEmail(find_user);
        if (append_user === null) {
            await message_dialog.current?.showMessage(`「${find_user}」 で登録されたユーザが見つかりませんでした`);
            setInputUser(find_user);
            return;
        }

        if (collaborator_list.exists(append_user)) {
            return;
        }

        setCollaboratorList(collaborator_list.addCollaborator(new Collaborator(append_user, find_user, append_type)));
    }

    const handleChangeCollaboratorType = (user: string, type: string) => {
        if (!isChangeableCollaboratorType(type)) {
            return;
        }

        setCollaboratorList(collaborator_list.changeType(user, type));
    }

    const handleApply = async () => {
        if (is_loading) {
            return;
        }

        await applyMapCollaborators(props.mapId, collaborator_list);
        props.onClose();
    }

    return (
        <dialog ref={ref} className="collaborator-dialog-dialog" onCancel={() => props.onClose()}>
            <div className="collaborator-dialog" onClick={e => e.stopPropagation()}>
                <h2 className="collaborator-dialog-title-area">{t("共有ユーザ設定")}</h2>
                <span className="collaborator-dialog-close-button" onClick={() => props.onClose()}/>

                <div className="collaborator-dialog-input-area">

                    <div className="collaborator-dialog-input-line">
                        <div className="collaborator-dialog-input-box">
                            <input type="text" placeholder={t("共有するユーザ、リストを入力")}
                                   value={input_user}
                                   onChange={(e) => setInputUser(e.target.value)}/>
                            <DropMenu containerClassName="collaborator-input-type"
                                      label={input_type === "editor" ? t("編集者") : t("閲覧者")}
                                      onChange={menu_name => isAppendableCollaboratorType(menu_name) && setInputType(menu_name)}>
                                <ul className="collaborator-dialog-dropdown-menu">
                                    <li data-drop_menu="editor">{t("編集者")}</li>
                                    <li data-drop_menu="viewer">{t("閲覧者")}</li>
                                </ul>
                            </DropMenu>
                        </div>
                        <button type="button" onClick={handleAddCollaborator} aria-disabled={is_loading}>
                            {t("リストに追加")}
                        </button>
                    </div>

                    {/*<a href="#">&gt; ユーザリスト管理</a>*/}
                </div>

                <div className="collaborator-dialog-listing-area">
                    <ul className="collaborator-dialog-listing">
                        {collaborator_list.list(c =>
                            <li key={c.User}>
                                <div><span title={is_dev() ? c.User : undefined}>{c.Email}</span></div>
                                <div>
                                    {c.isOwner
                                        ? <span>{t(c.TypeLabel)}</span>
                                        : <DropMenu containerClassName="collaborator-change-type"
                                                    label={t(c.TypeLabel)}
                                                    onChange={menu_name => handleChangeCollaboratorType(c.User, menu_name)}>
                                            <ul className="collaborator-dialog-dropdown-menu">
                                                <li data-drop_menu="editor">{t("編集者")}</li>
                                                <li data-drop_menu="viewer">{t("閲覧者")}</li>
                                                <li data-drop_menu='deleted'>{t("アクセス権を削除")}</li>
                                            </ul>
                                        </DropMenu>
                                    }
                                </div>
                            </li>
                        )}
                    </ul>

                </div>

                <div className="collaborator-dialog-button-area">
                    <button type="submit" onClick={handleApply} aria-disabled={is_loading}>
                        {t("設定を更新")}
                    </button>
                </div>
            </div>
            <MessageDialog ref={message_dialog} />
        </dialog>
    );
}
